import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../context/PortalAuthContext';
import { ApiError, authApi } from '../utils/api';

const registerSchema = z.object({
  role: z.enum(['student', 'educator']),
  first_name: z.string().trim().min(1, 'First name is required'),
  last_name: z.string().trim().min(1, 'Last name is required'),
  institution_id: z.string().min(1, 'Institution is required'),
  email: z.string().trim().email('Enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type RegisterFormValues = z.infer<typeof registerSchema>;
type RegisterLocationState = {
  preselectedRole?: 'student' | 'educator';
};

function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = usePortalAuth();
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const preselectedRole = (location.state as RegisterLocationState | null)?.preselectedRole;
  const {
    control,
    register: registerField,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      role: 'student',
      first_name: '',
      last_name: '',
      institution_id: '',
      email: '',
      password: '',
    },
  });

  const { data: institutionsResponse, isLoading: institutionsLoading } = useQuery({
    queryKey: ['auth', 'institutions'],
    queryFn: authApi.getInstitutions,
    staleTime: 5 * 60 * 1000,
  });

  const institutions = institutionsResponse?.institutions ?? [];

  useEffect(() => {
    if (preselectedRole === 'student' || preselectedRole === 'educator') {
      setValue('role', preselectedRole);
    }
  }, [preselectedRole, setValue]);

  const onSubmit = async (values: RegisterFormValues) => {
    setError(null);
    setInfo(null);

    try {
      const response = await register({
        role: values.role,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        password: values.password,
        institution_id: values.institution_id,
      });

      if (response.pendingApproval) {
        const message = response.message || 'Your educator account is pending administrator approval.';
        setInfo(message);
        navigate('/signin', { state: { message } });
        return;
      }

      navigate(values.role === 'educator' ? '/educator-portal' : '/student-portal');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Unable to register');
      }
    }
  };

  return (
    <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
      <Card sx={{ width: '100%', maxWidth: 560 }}>
        <CardContent>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Register
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Create a portal account.
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              {info && <Alert severity="info">{info}</Alert>}

              <FormControl fullWidth required>
                <InputLabel id="role-label">Role</InputLabel>
                <Controller
                  control={control}
                  name="role"
                  render={({ field }) => (
                    <Select
                      labelId="role-label"
                      label="Role"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value as 'student' | 'educator')}
                    >
                      <MenuItem value="student">Student</MenuItem>
                      <MenuItem value="educator">Educator</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="First Name"
                  error={Boolean(errors.first_name)}
                  helperText={errors.first_name?.message}
                  {...registerField('first_name')}
                  required
                  fullWidth
                />
                <TextField
                  label="Last Name"
                  error={Boolean(errors.last_name)}
                  helperText={errors.last_name?.message}
                  {...registerField('last_name')}
                  required
                  fullWidth
                />
              </Stack>

              <FormControl fullWidth required>
                <InputLabel id="institution-label">Institution</InputLabel>
                <Controller
                  control={control}
                  name="institution_id"
                  render={({ field }) => (
                    <Select
                      labelId="institution-label"
                      label="Institution"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={Boolean(errors.institution_id)}
                    >
                      <MenuItem value="">
                        <em>{institutionsLoading ? 'Loading institutions...' : 'Select institution'}</em>
                      </MenuItem>
                      {institutions.map((institution) => (
                        <MenuItem key={institution.institution_id} value={institution.institution_id}>
                          {institution.name} ({institution.domain})
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.institution_id && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.institution_id.message}
                  </Typography>
                )}
                <Typography color="text.secondary" variant="caption" sx={{ mt: 0.5, ml: 1.75, display: 'block' }}>
                  If your institution does not appear in the list, please{' '}
                  <a href="mailto:contact@almathema.com">contact us</a>.
                </Typography>
              </FormControl>

              <TextField
                label="Email"
                type="email"
                helperText={errors.email?.message}
                error={Boolean(errors.email)}
                {...registerField('email')}
                required
                fullWidth
              />

              <TextField
                label="Password"
                type="password"
                helperText={errors.password?.message ?? 'Minimum 8 characters'}
                error={Boolean(errors.password)}
                {...registerField('password')}
                required
                fullWidth
              />

              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? 'Creating account...' : 'Create Account'}
              </Button>

              <Button component={RouterLink} to="/signin" variant="text">
                Already have an account? Sign in
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default RegisterPage;
